import { z } from "zod";
import { MealPreferenceSchema } from "./guest";
/**
 * AI Cloud Function request/response schemas.
 * Used by both apps/web (typed client) and apps/functions (input + output validation).
 * The Cloud Function ALWAYS validates with these before applying side-effects.
 */
// ─── CSV column mapping (FRD §5.4.2) ─────────────────────────────────────────
export const CsvMapRequestSchema = z.object({
    weddingId: z.string(),
    columns: z.array(z.string()).min(1).max(50),
    sampleRows: z.array(z.record(z.string())).max(5),
});
export const CsvMapTargetFieldSchema = z.enum([
    "firstName",
    "lastName",
    "phone",
    "groupName",
    "mealPreference",
    "notes",
    "ignore",
]);
export const CsvMappingSchema = z.object({
    csvColumn: z.string(),
    targetField: CsvMapTargetFieldSchema,
    confidence: z.enum(["high", "medium", "low"]),
});
export const CsvMapResponseSchema = z.object({
    mappings: z.array(CsvMappingSchema),
    detectedEncoding: z.enum(["utf-8", "iso-8859-2", "unknown"]).optional(),
});
// ─── Image guest extraction (FRD §5.4.3) ─────────────────────────────────────
export const ExtractGuestsRequestSchema = z.object({
    weddingId: z.string(),
    imageStoragePath: z.string(),
});
export const ExtractedGuestSchema = z.object({
    firstName: z.string(),
    lastName: z.string().optional(),
    phone: z.string().optional(),
    mealPreference: MealPreferenceSchema.optional(),
    notes: z.string().optional(),
    confidence: z.enum(["high", "medium", "low"]),
});
export const ExtractGuestsResponseSchema = z.object({
    guests: z.array(ExtractedGuestSchema),
    overallConfidence: z.enum(["high", "medium", "low"]),
    rawText: z.string().optional(),
});
// ─── Seating generator (FRD §5.4.4) ──────────────────────────────────────────
export const SeatingPreferencesSchema = z.object({
    groupsTogether: z.enum(["yes", "partly", "no"]).default("yes"),
    conflictPairs: z.array(z.tuple([z.string(), z.string()])).max(10).default([]),
    vipTableId: z.string().nullable().optional(),
    vipGuestIds: z.array(z.string()).default([]),
    childrenWithParents: z.boolean().default(true),
    childrenAtDedicatedTable: z.boolean().default(false),
    mixSeating: z.boolean().default(false),
    accessibilityOuter: z.boolean().default(true),
});
export const GenerateSeatingRequestSchema = z.object({
    weddingId: z.string(),
    preferences: SeatingPreferencesSchema,
    lockedAssignments: z.record(z.string()).default({}),
});
export const SeatingAssignmentSchema = z.record(z.string(), z.object({
    tableId: z.string(),
    seatNumber: z.number().int().nonnegative(),
}));
export const ConstraintReportSchema = z.object({
    groupsTogether: z.boolean(),
    conflictsRespected: z.boolean(),
    vipTableHonoured: z.boolean(),
    unaccommodatedGuestIds: z.array(z.string()).default([]),
    emptySeats: z.number().int().nonnegative(),
});
export const GenerateSeatingResponseSchema = z.object({
    assignment: SeatingAssignmentSchema,
    report: ConstraintReportSchema,
});
// ─── Chat (FR-AI-CHAT-001) ───────────────────────────────────────────────────
export const ChatMessageSchema = z.object({
    id: z.string(),
    role: z.enum(["user", "assistant"]),
    content: z.string(),
    createdAt: z.string().datetime(),
});
export const ChatRequestSchema = z.object({
    weddingId: z.string(),
    message: z.string().min(1).max(4000),
    history: z.array(ChatMessageSchema).max(20).default([]),
});
export const ChatResponseSchema = z.object({
    reply: z.string(),
    remainingQuota: z.number().int().nonnegative(),
});
